import { useCallback, useEffect, useState } from 'react';

import { Tab } from '@headlessui/react';

import adminAPI from '@/api/admin';
import { ErrorAlert, SuccessAlert } from '@/components/Alert';
import Layout from '@/components/Layout';
import Modal from '@/components/Modals/Modal';
import BaseTab from '@/components/ProfilesAdminPage/BaseTab';
import OrgSearchDropdown from '@/components/ProfilesAdminPage/OrgSearchDropdown';
import PROFILE_TAB_CONFIGS from '@/components/ProfilesAdminPage/tabConfigs';
import TabList from '@/components/TabList';
import MESSAGES from '@/constants/messages-en';
import useAppState from '@/hooks/useAppState';

export default function ProfileListAdminPage() {
  const { profileType, selectProfileType } = useAppState();

  const [success, setSuccess] = useState('');
  const [error, setError] = useState(false);
  const [unconnectedCount, setUnconnectedCount] = useState<number>();
  const [connectedCount, setConnectedCount] = useState<number>();
  const [deletedCount, setDeletedCount] = useState<number>();
  const [allCount, setAllCount] = useState<number>();
  const [profileMoveIsOpen, setProfileMoveIsOpen] = useState(false);
  const [moveProfileIDs, setMoveProfileIDs] = useState<number[]>([]);
  const [moveOrgSearch, setMoveOrgSearch] = useState('');
  const [moveOrgID, setMoveOrgID] = useState(-1);
  const [isMoveLoading, setIsMoveLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refreshCounts = useCallback(async () => {
    const response = await adminAPI.countProfiles();
    setUnconnectedCount(response.data?.data?.unconnected);
    setConnectedCount(response.data?.data?.connected);
    setDeletedCount(response.data?.data?.deleted);
    setAllCount(response.data?.data?.all);
  }, []);

  const openMoveModal = useCallback((profileIDs: number[]) => {
    setMoveProfileIDs(profileIDs);
    setProfileMoveIsOpen(true);
  }, []);

  const handleMoveSuccess = () => {
    setIsMoveLoading(true);
    setSuccess('');
    setError(false);
    adminAPI
      .updateProfilesShared({
        ids: moveProfileIDs,
        profile: {
          organisation_id: moveOrgID,
        },
      })
      .then(() =>
        setSuccess(
          moveProfileIDs.length === 1
            ? MESSAGES.profile.organisation.success
            : MESSAGES.profile.organisation.successMultiple,
        ),
      )
      .catch(() => setError(true))
      .then(() => setIsMoveLoading(false))
      .then(() => refreshCounts())
      .then(() => setRefreshTrigger(prev => prev + 1))
      .finally(() => {
        setProfileMoveIsOpen(false);
        setMoveProfileIDs([]);
        setMoveOrgSearch('');
        setMoveOrgID(-1);
      });
  };

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  const configsWithCounts = PROFILE_TAB_CONFIGS.map(config => {
    let count;
    if (config.value === 'unconnected') count = unconnectedCount;
    else if (config.value === 'connected') count = connectedCount;
    else if (config.value === 'deleted') count = deletedCount;
    else if (config.value === 'all') count = allCount;
    return {
      name: config.name,
      value: config.value,
      count,
      color: config.badgeColor,
    };
  });

  return (
    <Layout pageName="Profiles">
      {success !== '' && <SuccessAlert message={success} />}
      {error && <ErrorAlert message={MESSAGES.error.generic} />}
      <Tab.Group
        defaultIndex={profileType}
        onChange={index => {
          setError(false);
          selectProfileType(index);
        }}
      >
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between w-full md:border-b border-gray-200">
          <div className="pt-4 md:pt-0 w-full md:w-auto">
            <TabList
              tabs={configsWithCounts}
              value={PROFILE_TAB_CONFIGS[profileType].value}
            />
          </div>
        </div>
        <Tab.Panels>
          {PROFILE_TAB_CONFIGS.map(config => (
            <BaseTab
              key={`${config.value}-${refreshTrigger}`}
              config={config}
              setSuccess={setSuccess}
              setError={setError}
              refreshCounts={refreshCounts}
              openMoveModal={openMoveModal}
            />
          ))}
        </Tab.Panels>
      </Tab.Group>
      <Modal
        isOpen={profileMoveIsOpen}
        setIsOpen={setProfileMoveIsOpen}
        dialogTitle={
          moveProfileIDs.length === 1
            ? MESSAGES.profile.organisation.heading
            : MESSAGES.profile.organisation.headingMultiple
        }
        dialogDescription={
          moveProfileIDs.length === 1
            ? MESSAGES.profile.organisation.description
            : MESSAGES.profile.organisation.descriptionMultiple
        }
        successButtonText={
          moveProfileIDs.length === 1
            ? MESSAGES.profile.organisation.heading
            : MESSAGES.profile.organisation.headingMultiple
        }
        onSuccess={handleMoveSuccess}
        onCancel={() => {
          setMoveProfileIDs([]);
          setMoveOrgSearch('');
          setMoveOrgID(-1);
        }}
        isDisabled={moveOrgID === -1}
        isLoading={isMoveLoading}
      >
        <div className="pt-4">
          <OrgSearchDropdown
            search={moveOrgSearch}
            setSearch={setMoveOrgSearch}
            onSelect={(orgID, displayText) => {
              setMoveOrgID(orgID);
              setMoveOrgSearch(displayText);
            }}
          />
        </div>
      </Modal>
    </Layout>
  );
}
